import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiErrorState } from '../../components/ApiErrorState.tsx';

interface Props {
  esAdmin: boolean;
  esUltimoMiembro: boolean;
  pendiente: boolean;
  error: unknown;
  onConfirmar: () => void | Promise<void>;
  onCancelar: () => void;
}

/**
 * Confirmación de abandono de liga.
 *
 * `<dialog>` nativo en vez de una capa a mano: trae gratis el foco atrapado dentro, el
 * cierre con Escape y el papel de diálogo modal para los lectores de pantalla. Todo eso
 * escrito a mano se hace mal casi siempre.
 *
 * Sobre el traspaso de administración: el backend promueve al miembro activo más
 * antiguo, y aquí solo se avisa de que "pasará a otro miembro". Explicar el criterio
 * exacto no ayuda a decidir y envejece mal si el criterio cambia.
 */
export function LeaveLeagueDialog({
  esAdmin,
  esUltimoMiembro,
  pendiente,
  error,
  onConfirmar,
  onCancelar,
}: Props) {
  const { t } = useTranslation();
  const dialogo = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    dialogo.current?.showModal();
  }, []);

  return (
    <dialog
      ref={dialogo}
      className="m-auto w-[min(24rem,calc(100vw-2rem))] rounded-xl border border-borde bg-superficie p-4 text-texto backdrop:bg-black/60"
      onCancel={(e) => {
        // Escape mientras se está saliendo dejaría el diálogo cerrado con la petición
        // en vuelo y sin nada que lo indique.
        if (pendiente) e.preventDefault();
        else onCancelar();
      }}
      data-test="dialogo-abandonar"
    >
      <h2 className="mb-2 text-lg font-bold">{t('leagues.leave.title')}</h2>
      <p className="mb-3 text-sm text-tenue">{t('leagues.leave.body')}</p>

      {esAdmin ? (
        <p className="aviso-atencion mb-3" data-test="aviso-admin">
          {esUltimoMiembro ? t('leagues.leave.lastMemberNotice') : t('leagues.leave.adminNotice')}
        </p>
      ) : null}

      <ApiErrorState error={error} />

      <div className="flex flex-col gap-2 sm:flex-row-reverse">
        <button
          type="button"
          className="btn-peligro flex-1"
          onClick={() => void onConfirmar()}
          disabled={pendiente}
          data-test="confirmar-abandonar"
        >
          {pendiente ? t('leagues.leave.pending') : t('leagues.leave.confirm')}
        </button>
        <button
          type="button"
          className="btn-secundario flex-1"
          onClick={onCancelar}
          disabled={pendiente}
          data-test="cancelar-abandonar"
        >
          {t('leagues.leave.cancel')}
        </button>
      </div>
    </dialog>
  );
}
